import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EditNoteIcon from '@mui/icons-material/EditNote'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill'
import RefreshIcon from '@mui/icons-material/Refresh'
import SetMealIcon from '@mui/icons-material/SetMeal'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DEVICE_TYPE, IClientFloors, ITableLock } from '../../all-types'
import { ITables, KITCHEN_ITEM_STATUS } from '../../all-types'
import { STORAGE_KEYS } from '../../constants'
import { setSelectedTable } from '../../redux-store'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import { getTableList, lockTable, unlockTable } from '../../services'
import { BillCounterTable } from './bill-counter-table'

interface FloorViewerProps {
  selectedFloor: IClientFloors
}

const getDineInTableStatus = (table: ITables) => {
  const items = table.billSession?.items || []
  const activeItems = items.filter(
    (item) => item.kitchInfo?.status !== KITCHEN_ITEM_STATUS.CANCELLED
  )

  if (activeItems.some((item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.READY)) {
    return {
      label: 'Items ready to serve',
      color: 'success.main',
      icon: <SetMealIcon fontSize="small" />,
    }
  }

  if (
    activeItems.length > 0 &&
    activeItems.every((item) =>
      [
        KITCHEN_ITEM_STATUS.IN_KITCHEN,
        KITCHEN_ITEM_STATUS.COOKING,
      ].includes(item.kitchInfo?.status)
    )
  ) {
    const isCooking = activeItems.some(
      (item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.COOKING
    )
    return {
      label: isCooking ? 'Items cooking' : 'Waiting in kitchen',
      color: 'warning.main',
      icon: <OutdoorGrillIcon fontSize="small" />,
    }
  }

  if (
    activeItems.length > 0 &&
    activeItems.every((item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.SERVED)
  ) {
    return {
      label: 'All items served',
      color: 'info.main',
      icon: <DoneAllIcon fontSize="small" />,
    }
  }

  if (activeItems.some((item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.IN_CART)) {
    return {
      label: 'Taking order',
      color: 'secondary.main',
      icon: <EditNoteIcon fontSize="small" />,
    }
  }

  return null
}

export const FloorViewer: React.FC<FloorViewerProps> = ({ selectedFloor }) => {
  const theme = useTheme()
  const allPlaces = useAppSelector((state) => state.preloadData.places)
  const allTables = useAppSelector((state) => state.preloadData.tables)
  const selectedTableId = useAppSelector(
    (state) => state.table.selectedTable?._id ?? null
  )
  const userId = useAppSelector((state) => state.authentication.userId) || localStorage.getItem(STORAGE_KEYS.USER_ID) || ''
  const username = useAppSelector((state) => state.authentication.username) || 'Main System'
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false)
  const [unlockingTableId, setUnlockingTableId] = useState<string | null>(null)
  const [isRefreshingTables, setIsRefreshingTables] = useState(false)

  const dispatcher = useAppDispatch()

  // Tracks the currently selected table id so the in-flight lockTable
  // callbacks below can detect if the user has since closed (or switched)
  // the table. Without this guard, a lock response that resolves *after*
  // the user clicks Close would re-dispatch `setSelectedTable(result.payload)`
  // and reopen the table the user just closed — which presents as the
  // "close button doesn't work / table keeps reopening" bug.
  const selectedTableIdRef = useRef<string | null>(selectedTableId)
  useEffect(() => {
    selectedTableIdRef.current = selectedTableId
  }, [selectedTableId])

  if (!selectedFloor || !selectedFloor.floorMap) {
    return <Typography>No floor data available.</Typography>
  }

  const floorTables = allTables.filter((t) => t.floorId === selectedFloor._id)
  const floorPlaces = allPlaces.filter((p) => p.floorId === selectedFloor._id)
  // Only surface tables that are locked by *someone else*. A user's own
  // lock should not appear in the "Locked tables" panel — to them, the
  // table simply behaves like one they have open.
  const lockedTables = useMemo(
    () =>
      allTables.filter(
        (table) =>
          !!table.tableLock?.lockedBy && table.tableLock.lockedBy !== userId
      ),
    [allTables, userId]
  )

  const showLockedMessage = (lock: ITableLock) => {
    window.alert(`This table is currently open by ${lock.lockedByName}.`)
  }

  const handleForceUnlock = async (tableId: string) => {
    try {
      setUnlockingTableId(tableId)
      const result = await dispatcher(unlockTable({ tableId, lockedBy: userId, force: true }))
      if (unlockTable.rejected.match(result)) {
        window.alert('Unable to unlock this table. Please refresh and try again.')
        return
      }
      await dispatcher(getTableList())
    } catch (error) {
      console.error(error)
      window.alert('Unable to unlock this table. Please refresh and try again.')
    } finally {
      setUnlockingTableId(null)
    }
  }

  const handleRefreshTables = async () => {
    try {
      setIsRefreshingTables(true)
      await dispatcher(getTableList())
    } finally {
      setIsRefreshingTables(false)
    }
  }

  const handleTableSelect = (table: typeof floorTables[number]) => {
    if (!table._id) return
    // Lock owned by someone else → show conflict; lock owned by us →
    // proceed straight into the table (don't surface a "locked" message
    // to the lock owner).
    if (table.tableLock?.lockedBy && table.tableLock.lockedBy !== userId) {
      showLockedMessage(table.tableLock)
      return
    }
    if (table.tableLock?.lockedBy && table.tableLock.lockedBy === userId) {
      dispatcher(setSelectedTable(table))
      return
    }

    // Optimistic open: switch to the products view immediately so the user
    // sees the table open instantly. The lock is acquired in the background.
    // If the server rejects (real conflict, network error), we revert.
    // Previously we awaited the lock RTT (~500ms) AND the products viewer
    // then ran getTableById on mount (~500ms more), giving a 1-2s lag
    // between click and any visible response.
    const intendedTableId = table._id
    dispatcher(setSelectedTable(table))

    // If the user closes (or switches) the table before the lock response
    // lands, every late `setSelectedTable(...)` in the closure below must
    // be skipped — otherwise the late call wins and "reopens" the table
    // the user just closed.
    const userStillOnThisTable = () =>
      selectedTableIdRef.current === intendedTableId

    void (async () => {
      try {
        const result = await dispatcher(lockTable({
          tableId: table._id!,
          lockedBy: userId,
          lockedByName: username,
          deviceType: DEVICE_TYPE.MAIN_SYSTEM,
        }))

        if (lockTable.fulfilled.match(result)) {
          if (!userStillOnThisTable()) return
          // Re-sync the selected table with the server's lock info.
          dispatcher(setSelectedTable(result.payload))
          return
        }

        const payload = result.payload as any
        if (payload?.locked && payload.lockInfo) {
          const conflictLock = payload.lockInfo as ITableLock
          if (conflictLock.lockedBy === userId) {
            if (!userStillOnThisTable()) return
            // Already ours from another tab/device — keep table open.
            dispatcher(setSelectedTable({ ...table, tableLock: conflictLock }))
            return
          }
          if (!userStillOnThisTable()) return
          // Real conflict: revert and inform the user.
          dispatcher(setSelectedTable(null))
          showLockedMessage(conflictLock)
          return
        }

        if (!userStillOnThisTable()) return
        // Generic failure: revert.
        dispatcher(setSelectedTable(null))
        window.alert('Unable to lock this table. Please refresh and try again.')
      } catch (error) {
        console.error(error)
        if (!userStillOnThisTable()) return
        dispatcher(setSelectedTable(null))
        window.alert('Unable to open this table. Please refresh and try again.')
      }
    })()
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        padding: 2,
        display: 'block',
        //backgroundColor: '#fafafa',
      }}
    >
      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<LockOpenIcon />}
          onClick={() => setUnlockDialogOpen(true)}
          disabled={lockedTables.length === 0}
        >
          Locked Tables ({lockedTables.length})
        </Button>
        <Tooltip title="Refresh dine-in tables">
          <span>
            <IconButton
              size="small"
              onClick={handleRefreshTables}
              disabled={isRefreshingTables}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.secondary.main,
                height: 34,
                width: 34,
              }}
            >
              {isRefreshingTables ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Unlock Tables</DialogTitle>
        <DialogContent dividers>
          {lockedTables.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No locked tables.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {lockedTables.map((table) => (
                <Box key={table._id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Box>
                      <Typography fontWeight={800}>
                        Table {table.tableNo} · {table.tableName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Locked by {table.tableLock?.lockedByName || 'Unknown'} · {table.tableLock?.deviceType || 'Unknown'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={<LockOpenIcon />}
                      disabled={!table._id || unlockingTableId === table._id}
                      onClick={() => table._id && handleForceUnlock(table._id)}
                    >
                      Unlock
                    </Button>
                  </Stack>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Paper
        elevation={4}
        sx={{
          position: 'relative',
          minWidth: selectedFloor.floorMap.width,
          minHeight: selectedFloor.floorMap.height,
          backgroundImage: `url(${selectedFloor.floorMap.bgImage})`,
          backgroundSize: 'auto', // Maintain original size
          backgroundRepeat: 'repeat', // Repeat the image
          backgroundPosition: 'top left',
          //border: '2px solid #3f51b5',
          //borderRadius: '8px',
        }}
      >
        {/* Tables */}
        {floorTables.map((table) => {
          const tableStatus = getDineInTableStatus(table)
          const lock = table.tableLock
          const isTakingOrder = Boolean(lock?.lockedBy)
          const lockLabel = lock?.lockedByName
            ? `Taking order: ${lock.lockedByName}`
            : 'Taking order'

          return (
            <Box
              key={table._id}
              onClick={() => handleTableSelect(table)}
              sx={{
                position: 'absolute',
                width: table.width,
                height: table.height,
                left: table.xAxis,
                top: table.yAxis,
                transform: `rotate(${table.rotation || 0}deg)`,
                zIndex: tableStatus || isTakingOrder ? 50 : 20,
                cursor: 'pointer',
                //pointerEvents: 'none',
              }}
            >
              <BillCounterTable
                {...table}
                tableColor={
                  table.billSession === null
                    ? theme.palette.freeTable.main
                    : theme.palette.occupiedTable.main
                }
              />

              {(tableStatus || isTakingOrder) && (
                <Stack
                  direction="row"
                  gap={0.75}
                  sx={{
                    position: 'absolute',
                    right: -8,
                    top: -8,
                    pointerEvents: 'none',
                    zIndex: 100,
                  }}
                >
                  {tableStatus && (
                    <Tooltip title={tableStatus.label}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: tableStatus.color,
                          border: `2px solid ${theme.palette.background.paper}`,
                          borderRadius: '999px',
                          boxShadow: theme.shadows[4],
                          color: '#fff',
                          display: 'flex',
                          height: 34,
                          justifyContent: 'center',
                          width: 34,
                        }}
                      >
                        {tableStatus.icon}
                      </Box>
                    </Tooltip>
                  )}

                  {isTakingOrder && (
                    <Tooltip title={lockLabel}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: 'primary.main',
                          border: `2px solid ${theme.palette.background.paper}`,
                          borderRadius: '999px',
                          boxShadow: theme.shadows[4],
                          color: '#fff',
                          display: 'flex',
                          height: 34,
                          justifyContent: 'center',
                          width: 34,
                        }}
                      >
                        <AssignmentIndIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Box>
          )
        })}

        {/* Places */}
        {floorPlaces.map((place, index) => (
          <Box
            key={place._id || index}
            sx={{
              position: 'absolute',
              width: place.placeMap?.width || 100,
              height: place.placeMap?.height || 100,
              left: place.placeMap?.xAxis || 0,
              top: place.placeMap?.yAxis || 0,
              //border: '2px dashed #42a5f5',
              backgroundColor: place.placeMap?.bgcolor || 'rgba(4, 4, 4, 0.75)',
              //borderRadius: '8px',
              zIndex: 10,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                color: place.placeMap?.textColor || 'primary.dark',
              }}
            >
              {place.placeName}
            </Typography>
          </Box>
        ))}

        {/* Props */}
        {selectedFloor.floorMap.floorProps.map((prop, index) => (
          <Box
            key={`prop-${index}`}
            sx={{
              position: 'absolute',
              width: prop.width,
              height: prop.height,
              left: prop.xAxis,
              top: prop.yAxis,
              transform: `rotate(${prop.rotation || 0}deg)`,
              pointerEvents: 'none',
              zIndex: 30,
            }}
          >
            <img
              src={prop.propImage}
              alt={prop.propName}
              width="100%"
              height="100%"
              style={{
                borderRadius: 4,
                objectFit: 'contain',
              }}
            />
          </Box>
        ))}
      </Paper>
    </Box>
  )
}
